<template>
  <div class="w-full max-w-xl p-6 space-y-8 sm:p-8 bg-white rounded-lg shadow dark:bg-gray-800">
    <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
      Edit Your Information
    </h2>
    <form class="mt-8 space-y-6" @submit.prevent="handleSubmit">

      <!-- Personal Information -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h3>
        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6 sm:col-span-3">
            <InputField id="username" label="Username" v-model="username" type="text" :disabled="true" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="email" label="Company Email" v-model="email" type="email" :disabled="true" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="first_name" label="First Name" v-model="formData.personal.first_name" type="text" :validationMessage="validationErrors.first_name" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="last_name" label="Last Name" v-model="formData.personal.last_name" type="text" :validationMessage="validationErrors.last_name" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="personal_email" label="Personal Email" v-model="formData.personal.personal_email" type="email" :validationMessage="validationErrors.personal_email" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="date_of_birth" label="Date of Birth" v-model="formData.personal.date_of_birth" type="date" :validationMessage="validationErrors.date_of_birth" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <SelectField id="gender" label="Gender" v-model="formData.personal.gender" :options="[{ value: '', text: 'Select gender' }, { value: 'male', text: 'Male' }, { value: 'female', text: 'Female' }]" :validationMessage="validationErrors.gender" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <div class="mb-2">
              <label for="country" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Country</label>
              <multiselect id="country" v-model="formData.personal.country" :options="countryOptions" :searchable="true" :allow-empty="true" placeholder="Select a country" :show-labels="false" />
              <div v-if="validationErrors.country" class="text-red-500 text-sm mt-1">{{ validationErrors.country }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Information -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6">
            <TextArea id="emergency_contacts" label="Emergency Contacts" v-model="formData.contact.emergency_contacts" placeholder="Separate by commas" :validationMessage="validationErrors.emergency_contacts" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="mobile_number" label="Mobile Number" v-model="formData.contact.mobile_number" type="text" :validationMessage="validationErrors.mobile_number" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="work_phone_number" label="Work Phone Number" v-model="formData.contact.work_phone_number" type="text" :validationMessage="validationErrors.work_phone_number" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="skype_username" label="Skype Username / Email" v-model="formData.contact.skype_username" type="text" :validationMessage="validationErrors.skype_username" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="telegram_nickname" label="Telegram Nickname" v-model="formData.contact.telegram_nickname" type="text" :validationMessage="validationErrors.telegram_nickname" />
          </div>
          <div class="col-span-6">
            <InputField id="address" label="Address" v-model="formData.contact.address" type="text" :validationMessage="validationErrors.address" />
          </div>
        </div>
      </div>

      <!-- Social Information -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Social Information</h3>
        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6 sm:col-span-3">
            <InputField id="facebook_url" label="Facebook URL" v-model="formData.socials.facebook_url" type="text" :validationMessage="validationErrors.facebook_url" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="linkedin_url" label="LinkedIn URL" v-model="formData.socials.linkedin_url" type="text" :validationMessage="validationErrors.linkedin_url" />
          </div>
        </div>
      </div>

      <!-- Position and Employment -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Position and Employment</h3>
        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6 sm:col-span-3">
            <InputField id="position" label="Position" v-model="formData.positions.position" type="text" placeholder="WordPress Developer, SEO specialist etc." :validationMessage="validationErrors.position" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="division" label="Division" v-model="formData.positions.division" type="text" placeholder="Development, SEO, Engineering etc." :validationMessage="validationErrors.division" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="location" label="Location" v-model="formData.positions.location" type="text" placeholder="California, USA / Kiev, Ukraine" :validationMessage="validationErrors.location" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="department" label="Department" v-model="formData.positions.department" type="text" :validationMessage="validationErrors.department" />
          </div>
          <div class="col-span-6">
            <label for="manager" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Manager</label>
            <multiselect id="manager" v-model="formData.positions.manager" :options="managerOptions" :multiple="true" track-by="value" label="text" placeholder="Select one or more managers" :close-on-select="false" :clear-on-select="false" :preserve-search="true" :hide-selected="true" :custom-label="customLabel" />
            <div v-if="validationErrors.manager" class="text-red-500 text-sm mt-1">{{ validationErrors.manager }}</div>
          </div>
        </div>
      </div>

      <!-- Employment Status -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Employment Status</h3>
        <div class="grid grid-cols-6 gap-6">
          <div class="col-span-6 sm:col-span-3">
            <InputField id="hired_on" label="Hired On" v-model="formData.employment_status.hired_on" type="date" :validationMessage="validationErrors.hired_on" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="employment_type" label="Employment Type" v-model="formData.employment_status.employment_type" type="text" placeholder="Full-time, Part-time etc." :validationMessage="validationErrors.employment_type" />
          </div>
          <div class="col-span-6 sm:col-span-3">
            <InputField id="probation_ends_on" label="Probation Ends On" v-model="formData.employment_status.probation_ends_on" type="date" :validationMessage="validationErrors.probation_ends_on" />
          </div>
        </div>
      </div>

      <!-- Compensation -->
      <div class="divide-y hover:divide-solid">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Compensation</h3>
        <div v-for="(comp, index) in formData.compensation" :key="index" class="space-y-4 mt-5 pt-5">
          <div class="grid grid-cols-6 gap-6">
            <div class="col-span-6 sm:col-span-3">
              <InputField id="effective_from" label="Effective From" v-model="comp.effective_from" type="date" :validationMessage="validationErrors['compensation.effective_from.' + index]" />
            </div>
            <div class="col-span-6 sm:col-span-3">
              <InputField id="amount" label="Amount" v-model="comp.amount" type="text" :validationMessage="validationErrors['compensation.amount.' + index]" />
            </div>
            <div class="col-span-6 sm:col-span-3">
              <SelectField id="per" label="Per" v-model="comp.per" :options="[{ value: '', text: 'Select' }, { value: 'hour', text: 'Hour' }, { value: 'day', text: 'Day' }, { value: 'week', text: 'Week' }, { value: 'month', text: 'Month' }, { value: 'year', text: 'Year' }]" :validationMessage="validationErrors['compensation.per.' + index]" />
            </div>
            <div class="col-span-6 sm:col-span-3">
              <SelectField id="overtime" label="Overtime" v-model="comp.overtime" :options="[{ value: '', text: 'Select' }, { value: 'yes', text: 'Yes' }, { value: 'no', text: 'No' }]" :validationMessage="validationErrors['compensation.overtime.' + index]" />
            </div>
            <div class="col-span-6 sm:col-span-3">
              <InputField id="pay_schedule" label="Pay Schedule" v-model="comp.pay_schedule" type="text" placeholder="Once per month, Twice per month etc." :validationMessage="validationErrors['compensation.pay_schedule.' + index]" />
            </div>
          </div>
        </div>
        <Button @click="addCompensation" label="Add Compensation" type="button" />
      </div>

      <!-- Save Changes Button -->
      <div class="mt-6">
        <Button label="Save Changes" type="submit" />
      </div>
    </form>
  </div>
  <!-- Status Informer -->
  <transition name="slide-fade">
    <div v-if="statusVisible" class="status-informer fixed right-2 bottom-2 group flex flex-col items-center justify-center rounded-xl bg-teal-200 p-2.5 hover:bg-teal-100 dark:bg-teal-900 dark:hover:bg-teal-800 cursor-pointer">
      {{ statusMessage }}
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import InputField from './FormComponents/InputField';
import TextArea from './FormComponents/TextArea';
import Button from './FormComponents/Button';
import SelectField from './FormComponents/SelectField';
import Multiselect from 'vue-multiselect';
import { countries } from '../data/countries';

export default {
  components: {
    InputField,
    TextArea,
    Button,
    SelectField,
    Multiselect,
  },
  data() {
    return {
      isLoading: true,
      username: '',
      email: '',
      selectedFile: null,
      previewImageUrl: null, // Start with null to trigger the placeholder initially
      statusVisible: false,  // Controls the visibility of the status informer
      statusMessage: '',     // Holds the status message to be displayed
      formData: {
        personal: {
          first_name: '',
          last_name: '',
          personal_email: '',
          date_of_birth: '',
          gender: '',
          country: '',
        },
        contact: {
          emergency_contacts: '',
          mobile_number: '',
          work_phone_number: '',
          telegram_nickname: '',
          skype_username: '',
          address: '',
        },
        socials: {
          facebook_url: '',
          linkedin_url: ''
        },
        positions: {
          position: '',
          division: '',
          location: '',
          department: '',
          manager: []
        },
        employment_status: {
          hired_on: '',
          employment_type: '',
          probation_ends_on: ''
        },
        compensation: [
          {
            effective_from: '',
            amount: '',
            per: '',
            overtime: '',
            pay_schedule: ''
          }
        ]
      },
      countryOptions: countries,
      managerOptions: [],
      validationErrors: {}
    };
  },
  async mounted() {
    // Set nonce header for authenticated REST API requests
    if (window.vacationData?.nonce) {
      axios.defaults.headers.common['X-WP-Nonce'] = window.vacationData.nonce;
    }
    await this.fetchManagers();
    await this.loadUserInfo();
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    async loadUserInfo() {
      this.isLoading = true;

      try {
        const userId = document.getElementById('edit-page__user-id').value;
        const response = await axios.get(`/wp-json/custom/v1/get-user-info?user_id=${userId}`);
        const data = response.data;

        // Set username and email
        this.username = data.username || '';
        this.email = data.email || '';

        const managers = await this.fetchManagers();

        // Directly replace the formData object (use optional chaining for null safety)
        const personal = data.formData?.personal || {};
        const contact = data.formData?.contact || {};
        const socials = data.formData?.socials || {};
        const positions = data.formData?.positions || {};
        const employment_status = data.formData?.employment_status || {};
        const compensation = data.formData?.compensation;

        this.formData = {
          personal: {
            first_name: personal.first_name || '',
            last_name: personal.last_name || '',
            personal_email: personal.personal_email || '',
            date_of_birth: this.formatDate(personal.date_of_birth) || '',
            gender: personal.gender || '',
            country: personal.country || '',
          },
          contact: {
            emergency_contacts: contact.emergency_contacts || '',
            mobile_number: contact.mobile_number || '',
            work_phone_number: contact.work_phone_number || '',
            skype_username: contact.skype_username || '',
            telegram_nickname: contact.telegram_nickname || '',
            address: contact.address || '',
          },
          socials: {
            facebook_url: socials.facebook_url || '',
            linkedin_url: socials.linkedin_url || '',
          },
          positions: {
            position: positions.position || '',
            division: positions.division || '',
            location: positions.location || '',
            department: positions.department || '',
            manager: managers.filter(manager =>
              (positions.manager || []).includes(manager.value)
            )
          },
          employment_status: {
            hired_on: this.formatDate(employment_status.hired_on) || '',
            employment_type: employment_status.employment_type || '',
            probation_ends_on: this.formatDate(employment_status.probation_ends_on) || '',
          },
          compensation: Array.isArray(compensation)
            ? compensation.map(comp => ({
              effective_from: this.formatDate(comp.effective_from) || '',
              amount: comp.amount || '',
              per: comp.per || '',
              overtime: comp.overtime || '',
              pay_schedule: comp.pay_schedule || '',
            }))
            : [],
        };

        // console.log('Updated formData:', this.formData);
      } catch (error) {
        console.error('Failed to load user info:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchManagers() {
      try {
        const response = await axios.get('/wp-json/custom/v1/managers');
        const managers = response.data.map(manager => ({
          value: manager.id,
          text: manager.name
        }));
        this.managerOptions = managers; // Update managerOptions for the dropdown
        return managers; // Explicitly return the data
      } catch (error) {
        console.error('Failed to fetch managers:', error);
        return []; // Return empty array in case of error
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      const userId = document.getElementById('edit-page__user-id').value;
      const payload = {
        user_id: userId,
        username: this.username,
        email: this.email,
        formData: this.formData
      };

      try {
        const response = await axios.post('/wp-json/custom/v1/update-user-info', payload);
        this.showStatusMessage('User information updated successfully.');
      } catch (error) {
        this.showStatusMessage('Failed to update user information.', true);
        console.error('Failed to update user information:', error.response ? error.response.data : error.message);
      }
    },
    validateForm() {
      this.validationErrors = {};

      // if (!this.username) {
      //   this.validationErrors.username = "Username is required.";
      // }
      // if (!this.email) {
      //   this.validationErrors.email = "Email is required.";
      // } else if (!this.email.includes('@clixsy.com')) {
      //   this.validationErrors.email = "Please use a valid company email ending with @clixsy.com.";
      // }

      // Additional validation logic here...

      return Object.keys(this.validationErrors).length === 0;
    },
    addCompensation() {
      if (!Array.isArray(this.formData.compensation)) {
        this.formData.compensation = [];
      }
      this.formData.compensation.push({
        effective_from: '',
        amount: '',
        per: '',
        overtime: '',
        pay_schedule: ''
      });
    }
    ,
    customLabel({ text }) {
      return `${text}`;
    },
    showStatusMessage(message, isError = false) {
      this.statusMessage = message;
      this.statusVisible = true;
      setTimeout(() => {
        this.statusVisible = false;
      }, 3000);
    }
  }
};
</script>

<style scoped>
.status-informer {
  transition: opacity 0.3s, transform 0.3s;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.slide-fade-enter,
.slide-fade-leave-to

/* .slide-fade-leave-active in <2.1.8 */
  {
  opacity: 0;
  transform: translateX(20px);
}
</style>
